import React, { useState, useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import ButtonVariant from "app/components/Button/ButtonVariant";
import PhoneInput from "react-phone-input-2";
import { fetchPost } from "app/utils/request";
import ModalLayoutWithTitle from "app/components/Layouts/ModalLayoutWithTitle";

interface IProps {
  isOpen: boolean;
  modalHandler: () => void;
  setOpenInfoModal: any;
  setModalContent: any;
  facilityId: string;
  onContactAdded: () => void;
}

interface IContactRow {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_country: string;
  role: string;
}

interface IContactError {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

const DEFAULT_CONTACT = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  phone_country: "",
  role: "athlete",
};

const AddNewContactManuallyModal = ({
  isOpen,
  modalHandler,
  setOpenInfoModal,
  setModalContent,
  facilityId,
  onContactAdded,
}: IProps) => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<IContactRow[]>([DEFAULT_CONTACT]);
  const [errors, setErrors] = useState<IContactError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const contentContainer = contentContainerRef.current;
    if (!contentContainer) {
      return undefined;
    }

    const repositionDropdowns = () => {
      const openDropdowns = contentContainer.querySelectorAll(
        ".flag-dropdown.open",
      );

      openDropdowns.forEach((dropdown) => {
        const countryList = dropdown.querySelector(".country-list");
        if (countryList && countryList instanceof HTMLElement) {
          const rect = dropdown.getBoundingClientRect();
          countryList.style.top = `${rect.bottom + 2}px`;
          countryList.style.left = `${rect.left}px`;
        }
      });
    };

    const handleScroll = () => {
      repositionDropdowns();
    };

    contentContainer.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll, true);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains("flag-dropdown")) {
            setTimeout(repositionDropdowns, 10);
          }
        }
      });
    });

    observer.observe(contentContainer, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
      childList: true,
    });

    window.addEventListener("resize", repositionDropdowns);

    return () => {
      contentContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", repositionDropdowns);
      observer.disconnect();
    };
  }, [isOpen]);

  const validateContact = (contact: IContactRow): IContactError => {
    const contactErrors: IContactError = {};

    const hasPersonalData =
      contact.first_name.trim() || contact.last_name.trim();

    if (hasPersonalData) {
      const hasValidEmail =
        contact.email.trim() && emailRegex.test(contact.email);
      const hasValidPhone = contact.phone.trim();

      if (!hasValidEmail && !hasValidPhone) {
        contactErrors.email = "error";
        contactErrors.phone = "error";
      } else {
        if (contact.email.trim() && !emailRegex.test(contact.email)) {
          contactErrors.email = "error";
        }
      }
    }

    return contactErrors;
  };

  const validateAllContacts = (): boolean => {
    const newErrors: IContactError[] = [];
    let isValid = true;

    const contactsWithData = contacts.filter(
      (contact) => contact.first_name.trim() || contact.last_name.trim(),
    );

    if (
      contactsWithData.length === 0 ||
      contacts.length !== contactsWithData.length
    ) {
      setModalContent({
        title: t("Error"),
        content: t("First Name and Last Name are mandatory."),
        status: "error",
      });
      setOpenInfoModal(true);

      return false;
    } else {
      contactsWithData.forEach((contact) => {
        if (!contact.first_name || !contact.last_name) {
          setModalContent({
            title: t("Error"),
            content: t("First Name and Last Name are mandatory."),
            status: "error",
          });
          setOpenInfoModal(true);

          return false;
        }

        return false;
      });
    }

    contacts.forEach((contact, index) => {
      const contactErrors = validateContact(contact);
      newErrors[index] = contactErrors;
      if (Object.keys(contactErrors).length > 0) {
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleContactChange = (
    index: number,
    field: keyof IContactRow,
    value: string,
    data?: any,
  ): void => {
    setContacts((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === "phone" && data
          ? { phone_country: data.countryCode }
          : {}),
      };
      return updated;
    });

    if (errors[index]?.[field]) {
      setErrors((prev) => {
        const newErrors = [...prev];
        if (newErrors[index]) {
          delete newErrors[index][field];
        }
        return newErrors;
      });
    }
  };

  const handleAddContacts = async () => {
    if (isLoading) {
      return;
    }

    if (!validateAllContacts()) {
      return;
    }

    setIsLoading(true);

    const validContacts = contacts.filter(
      (contact) => contact.first_name.trim() || contact.last_name.trim(),
    );

    let successCount = 0;
    let errorCount = 0;
    const failedContacts: string[] = [];
    const fetchErrors: any[] = [];

    const promises = validContacts.map(async (contact, index) => {
      const params = { contact };

      try {
        await fetchPost(`/facilities/${facilityId}/contacts`, params);
        successCount += 1;
      } catch (error) {
        fetchErrors.push(error.errors[0]);
        console.error("Failed to add contact:", error);
        errorCount += 1;

        let errorMessage = "";
        if (error && typeof error === "object") {
          if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            errorMessage = Object.entries(errors)
              .map(([_, messages]) => {
                if (Array.isArray(messages)) {
                  return messages.join(", ");
                }
                return messages;
              })
              .join(", ");
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
        }

        const identifier =
          contact.email ||
          `${contact.first_name} ${contact.last_name}`.trim() ||
          `Contact ${index + 1}`;

        const fullErrorMessage = errorMessage
          ? `${identifier}: ${errorMessage}`
          : identifier;

        failedContacts.push(fullErrorMessage);
      }
    });

    await Promise.all(promises);

    setIsLoading(false);
    const sameEmailPresent: boolean = fetchErrors.some(
      (error) => error === "email_contact_already_exists",
    );
    const samePhonePresent: boolean = fetchErrors.some(
      (error) => error === "phone_contact_already_exists",
    );
    // The backend rejects contacts whose email/phone belongs to a coach account
    // (contact lists are for athletes and parents only).
    const coachContactPresent: boolean = fetchErrors.some(
      (error) => error === "user_id_already_added_as_coach",
    );

    if (successCount > 0 && errorCount === 0) {
      setModalContent({
        title: t("Contacts Added"),
        content: t("You successfully added {{count}} {{contactText}}", {
          count: successCount,
          contactText: t("contact", { count: successCount }),
        }),
        status: "success",
      });
      setContacts([DEFAULT_CONTACT]);
      setErrors([]);
      modalHandler();
      setOpenInfoModal(true);
      onContactAdded?.();
    } else if (successCount > 0 && errorCount > 0) {
      setModalContent({
        title: t("Partial Success"),
        content: t(
          "You successfully added {{successCount}} {{successText}}. Failed to add {{errorCount}}:\n{{failedList}}",
          {
            successCount,
            successText: successCount > 1 ? t("contacts") : t("contact"),
            errorCount,
            failedList: sameEmailPresent
              ? "Email already in use."
              : samePhonePresent
                ? "Phone Number already in use."
                : coachContactPresent
                  ? t(
                      "This email or phone number belongs to a coach's account. Contact lists are for athletes and parents only — coaches can't be added.",
                    )
                  : failedContacts.join("\n"),
          },
        ),
        status: "warning",
      });
      setOpenInfoModal(true);
      onContactAdded?.();
    } else {
      if (sameEmailPresent) {
        setModalContent({
          title: t("Email already in use."),
          status: "error",
        });
      } else if (samePhonePresent) {
        setModalContent({
          title: t("Phone Number already in use."),
          status: "error",
        });
      } else if (coachContactPresent) {
        setModalContent({
          title: t("Can't Add a Coach as a Contact"),
          content: t(
            "This email or phone number belongs to a coach's account. Contact lists are for athletes and parents only — coaches can't be added.",
          ),
          status: "error",
        });
      } else {
        setModalContent({
          title: t("Error Adding Contacts"),
          content: t("Failed to add contacts:\n{{failedList}}", {
            failedList: failedContacts.join("\n"),
          }),
          status: "error",
        });
      }

      setOpenInfoModal(true);
    }
  };

  const handleCancel = () => {
    if (isLoading) {
      return;
    }
    setContacts([DEFAULT_CONTACT]);
    setErrors([]);
    modalHandler();
  };

  const addNewContact = () => {
    setContacts((prev) => [...prev, DEFAULT_CONTACT]);
  };

  return (
    <ModalLayoutWithTitle
      isOpen={isOpen}
      onClose={handleCancel}
      headerTitle={t("Add Contacts")}
    >
      <div className={styles.container}>
        <div className={styles.top_container}>
          <div className={styles.content_container} ref={contentContainerRef}>
            <table className={styles.contacts_add_roster}>
              <thead className={styles.contacts_add_roster_header}>
                <tr>
                  <th className={styles.contacts_add_roster_label}>
                    {t("First Name")}
                  </th>
                  <th className={styles.contacts_add_roster_label}>
                    {t("Last Name")}
                  </th>
                  <th className={styles.contacts_add_roster_label}>
                    {t("Email")}
                  </th>
                  <th className={styles.contacts_add_roster_label}>
                    {t("Phone Number")}
                  </th>
                </tr>
              </thead>

              <tbody className={styles.contacts_add_roster_list}>
                {contacts.map((contact: IContactRow, index: number) => (
                  <tr
                    key={index}
                    className={styles.contacts_add_roster_control_group}
                  >
                    <td
                      className={styles.contacts_add_roster_control}
                      data-label={t("First Name")}
                    >
                      <input
                        type="text"
                        data-testid="contact-first-name"
                        value={contact.first_name}
                        className={`${styles.input} ${errors[index]?.first_name ? styles.input_error : ""}`}
                        onChange={(e) =>
                          handleContactChange(
                            index,
                            "first_name",
                            e.target.value,
                          )
                        }
                        disabled={isLoading}
                      />
                    </td>

                    <td
                      className={styles.contacts_add_roster_control}
                      data-label={t("Last Name")}
                    >
                      <input
                        type="text"
                        data-testid="contact-last-name"
                        value={contact.last_name}
                        className={`${styles.input} ${errors[index]?.last_name ? styles.input_error : ""}`}
                        onChange={(e) =>
                          handleContactChange(
                            index,
                            "last_name",
                            e.target.value,
                          )
                        }
                        disabled={isLoading}
                      />
                    </td>

                    <td
                      className={styles.contacts_add_roster_control}
                      data-label={t("Email")}
                    >
                      <input
                        type="email"
                        data-testid="contact-email"
                        value={contact.email}
                        className={`${styles.input} ${errors[index]?.email ? styles.input_error : ""}`}
                        onChange={(e) =>
                          handleContactChange(index, "email", e.target.value)
                        }
                        disabled={isLoading}
                      />
                    </td>

                    <td
                      className={styles.contacts_add_roster_control}
                      data-label={t("Phone Number")}
                    >
                      <PhoneInput
                        value={contact.phone}
                        onChange={(value, data) =>
                          handleContactChange(index, "phone", value, data)
                        }
                        inputClass={`${styles.input} ${errors[index]?.phone ? styles.input_error : ""}`}
                        country={"us"}
                        disabled={isLoading}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <span
            className={styles.add_member_btn}
            onClick={isLoading ? undefined : addNewContact}
          >
            {t("+ Add more")}
          </span>
        </div>

        <div className={styles.buttons_container}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={handleCancel}
            disabled={isLoading}
          />
          <ButtonVariant
            title={isLoading ? t("Adding...") : t("Add")}
            buttonType="primary"
            onClickFunc={handleAddContacts}
            disabled={isLoading}
          />
        </div>
      </div>
    </ModalLayoutWithTitle>
  );
};

export default AddNewContactManuallyModal;
